import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {
    AndConstraint,
    Column,
    ComparisonConstraint,
    ConfiguratorDataViewService,
    ConfiguratorTableService,
    Constraint,
    DataViewDto,
    EngineDataQueryService,
    EngineTagEventPersonalService,
    FieldTable,
    FieldTableDto,
    FullTextSearchConstraint,
    HavingCountTable,
    ObjectQueryFilter,
    OrConstraint,
    Ordering,
    PropertyValue,
    ReplaceHeaderTransformConf,
    Table,
    TableJointure,
    Value
} from '@tj/tj-dmp-lib-api';

import {LazyLoadEvent, MessageService, SelectItem, SortMeta, TreeNode} from 'primeng/api';
import * as _ from 'lodash';
import {CompareService} from '../data-compare/compare.service';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {BuildComponentService} from '../../../services/build-component.service';
import {DynRawDetailDialogComponent} from './dyn-raw-detail-dialog/dyn-raw-detail-dialog.component';
import {RawDetailInfoInterface} from './raw-detail-Info.interface';
import {DynRawNewDialogComponent} from './dyn-raw-new-dialog/dyn-raw-new-dialog.component';
import {RawNewInfoInterface} from './raw-new-Info.interface';
import {FilterBuilderService} from '../../../services/filter-builder.service';
import {ExpandDataComponent} from '../expand-data/expand-data.component';
import {TranslateService} from '@ngx-translate/core';
import {
    ManualAcquisitionBulkDialogComponent,
    ManualAcquisitionBulkDialogData
} from './manual-acquisition-bulk-dialog/manual-acquisition-bulk-dialog.component';
import {
    TagResultsDialogComponent,
    TagResultsDialogData,
    TagResultsDialogResult,
    TagRuleTarget
} from './tag-results-dialog/tag-results-dialog.component';
import {EMPTY, Observable, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {isTagJobFinished, tagJobPercent, TagJobService, TagJobStatus} from '../../../services/tag-job.service';
import CategoryFieldEnum = FieldTable.CategoryFieldEnum;

export interface Paginator {
    start: number;
    pageSize: number;
}

export interface ColumnEditState {
    editing: boolean;
    editValue: string;
}

export interface ColumnEditStateMap {
    [columnKey: string]: ColumnEditState;
}

/** Clé, dans une ligne, des noms de tags de son contact (comme '@id', hors colonnes de la vue). */
const ROW_TAGS_KEY = '@tags';
const TABLE_PERSONAL = 'personal';
/** Toast du résultat d'un tag ponctuel quand il propose « Automatiser » (bouton dans le message). */
const TAG_RESULT_TOAST = 'tag-result';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'dmp-table-meta',
    templateUrl: './table-meta.component.html',
    styleUrls: ['./table-meta.component.scss'],
    providers: [MessageService, DialogService, BuildComponentService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TableMetaComponent implements OnChanges, OnDestroy, AfterViewInit {

    constructor(
        protected tableResourceService: ConfiguratorTableService,
        protected compareService: CompareService,
        protected configuratorDataViewService: ConfiguratorDataViewService,
        protected engineDataQueryService: EngineDataQueryService,
        private engineTagEventPersonalService: EngineTagEventPersonalService,
        protected messageService: MessageService,
        protected dialogService: DialogService,
        private cdref: ChangeDetectorRef,
        private filterBuilderService: FilterBuilderService,
        private sanitizer: DomSanitizer,
        private router: Router,
        private translate: TranslateService,
        private tagJobService: TagJobService
    ) {
        // this.init();

        this.loading = false;
        this.rowByPages = [
            {value: 10},
            {value: 20},
            {value: 30},
            {value: 40},
            {value: 50},
            {value: 100},
            {value: 200},
            {value: 500},
            {value: 1000},
        ];
        this.initPaginator();
    }

    get columns() {
        return [...this.displayables, ...this.selectCols];
    }

    get isCreateButtonDisabled(): boolean {
        return !this.dataView?.defaultFieldCreation || this.dataView.defaultFieldCreation.length === 0;
    }

    get createButtonTooltip(): string {
        if (this.isCreateButtonDisabled) {
            return this.translate.instant('components.table-meta.createDisabledTooltip');
        }
        return this.translate.instant('components.table-meta.createButtonTooltip');
    }

    public isModeView = true;
    public isModeExport = true;
    @Input() mode: 'MODE_VIEW' | 'MODE_EXPORT' = 'MODE_VIEW';
    @Input() criterTemplate: TemplateRef<any>;
    @Input() datastore: string;
    @Input() partner: string;
    /** Si renseigné, les données sont restreintes à ces affiliés (aperçu d'un export groupé) ; la vue reste celle de `partner`. */
    @Input() partners: string[];
    /** Export groupé de contacts sur plusieurs affiliés : colonne figée des affiliés de chaque contact. */
    @Input() affiliatesColumn = false;
    /** Case de l'en-tête de cette colonne : l'ajouter aussi au fichier exporté. */
    @Input() affiliatesInFile = false;
    @Output() affiliatesInFileChange = new EventEmitter<boolean>();
    @Input() codeView: string;
    @Input() labelAddItem: string;
    @Input() presetCols: string[];
    /**
     * permet d'afficher une liste d'accordeon des jointures
     */
    @Input() displaySubQuery = false;
    @Input() public customHeader: ReplaceHeaderTransformConf[] = [];
    /** Arbre des filtres de l'écran, gardé avec un tag en masse pour l'historique des tags. */
    @Input() filterTree: TreeNode<any>;
    /** Preset chargé : après un tag ponctuel, on propose de l'automatiser sur lui. */
    @Input() activePreset: { id: string; name: string } | null = null;
    /** « Automatiser » cliqué dans le message de résultat : la page le transmet à la barre de preset. */
    @Output() automateRequested = new EventEmitter<{ status: boolean; tagIds: number[] }>();

    @Output() onQueryUpdate = new EventEmitter();
    @Output() onDataLoaded = new EventEmitter<any[]>();
    @Output() onSelectedColumnsChange = new EventEmitter<FieldTableDto[]>();
    @Output() public onCustomHeaderChange = new EventEmitter<ReplaceHeaderTransformConf[]>();

    @ViewChild(ExpandDataComponent) expandDataComponent: ExpandDataComponent;
    @ViewChild('searchInput') searchInput: any;

    // liste de toutes les colonnes de la clause FROM
    cols: FieldTableDto[] = [];
    // liste de toutes les colonnes pouvant être affiché de la caluse SELECT
    selectCols: FieldTableDto[] = [];
    // ligne de données
    rowsData: Array<any>;
    // nombre de résultat présent en base
    totalRecords = 0;

    // premier noeud disposant de plusieurs jointures
    tableAvecPlusieursJointures: any = null;
    /**
     * colonne afficher avec sauvegarde localstorage
     */
    displayables: string[] = [];
    /**
     * colonne afficher provenant du seveur
     */
    displayablesOrigin: string[] = [];
    paginator: Paginator;
    rowByPages: SelectItem[];
    selectedRowByPage: SelectItem;
    codeTables: string[] = [];
    tables: Table[];
    /** liste des colonnes provenant de la bdd*/
    private selectColumns: Column[];
    loading = false;
    display = false;
    currentConstraint: Constraint = null;
    /** Liste des champs à afficher pour l'affichage de la zone d'expand */
    expandSortField: string[] = [];
    compareSortField: string[] = [];
    reorderableRow = false;
    /** critère de Trie de la recherche */
    orderings: Ordering[] = [];
    having: HavingCountTable[] = [];
    displayComparateur = false;
    labelComparateur = '';
    disableComparateur = false;
    displayCreateDisabledDialog = false;
    dataView: DataViewDto;
    // template context
    ref: DynamicDialogRef;
    /** liste des colonnes sélectionnées */
    colsSelected: FieldTableDto[];
    /**
     * Liste des colonnes disponibles
     */
    public selectColsGrouped: any[] = [];
    private idDataview: string;
    private currentQueryFilterCache: ObjectQueryFilter;
    multiSortMeta: SortMeta[] = [];
    expandedRowKeys: { [key: string]: boolean } = {};

    columnManagerDialog = false;
    visibleColsSelection: FieldTableDto[] = [];
    columnSearchTerm = '';

    // Recherche globale dans toutes les colonnes
    globalSearchText: string = '';

    /** La vue joint les contacts : on peut afficher et poser leurs tags. */
    personalInView = false;
    /**
     * Colonne « Tags » : les tags du contact de chaque ligne (le client, pour une commande). Elle n'est
     * pas une colonne de la vue : ni exportée, ni enregistrée dans un preset, ni triable, ni cherchable.
     * Les presets et les exports serveur relisent les colonnes de la vue, qui ignorent ce champ calculé.
     */
    showTagsColumn = false;
    /** Tag en masse en cours sur cette page : son avancement s'affiche sur le bouton « Taguer ». */
    tagJob: TagJobStatus = null;
    private tagJobSub: Subscription = null;

    private columnEditState: ColumnEditStateMap = {};
    private dataFetched = false;

    updateSelectColsGrouped() {
        const result = this.selectCols
            .filter(f => FieldTableDto.CategoryFieldEnum.TECHNICAL !== f.categoryField && f.tableForFk == null)
            .reduce((acc, curr) => {
                const table = acc.find(el => el.value === curr.table);
                if (table) {
                    table.items.push(curr);
                } else {
                    acc.push({
                        label: curr.table,
                        value: curr.table,
                        items: [curr]
                    });
                }
                return acc;
            }, []);
        this.selectColsGrouped = result;
    }

    ngAfterViewInit() {
        if (this.dataFetched) {
            setTimeout(() => {
                this.display = true;
                this.cdref.detectChanges();
            }, 0);
        }
    }

    ngOnDestroy(): void {
        if (this.ref) {
            this.ref.close();
        }
        // la tâche de tag continue côté engine ; seul son suivi s'arrête avec la page
        this.tagJobSub?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.datastore || changes.codeView || changes.partner) {
            this.init();
        }
    }

    init() {
        if (this.mode === 'MODE_VIEW') {
            this.isModeView = true;
            this.isModeExport = false;
        } else {
            this.isModeView = false;
            this.isModeExport = true;
        }
        if (this.datastore && this.codeView) {
            this.display = false;
            this.initPaginator();
            this.currentQueryFilterCache = null;
            this.rowsData = [];
            this.expandedRowKeys = {};
            this.totalRecords = 0;
            this.loading = false;

            this.configuratorDataViewService
                .getDataViewUsingGET(this.datastore, this.partner, this.codeView, true)
                .subscribe((res) => {
                    this.dataView = res;
                    this.idDataview = res.id;
                    // table
                    this.codeTables = [res.table.tableName];
                    this.setTableRecursif(res.table.join, res.table);
                    this.personalInView = this.isModeView && this.codeTables.includes(TABLE_PERSONAL);
                    this.showTagsColumn = this.personalInView && this.readShowTagsColumn();
                    // select
                    this.selectColumns = (res as any).selectColumns;
                    // colonne afficher provenant du seveur
                    this.displayablesOrigin = [];
                    res.defaultDisplayFields.forEach((col) => {
                        this.displayablesOrigin.push(col.propertyName);
                    });
                    this.displayables = this.displayablesOrigin;
                    this.loadDataOnInit();

                });
        }
    }

    loadDataOnInit() {
        this.tableResourceService
            .getTablesUsingGET(this.datastore, this.codeTables)
            .subscribe((result) => {
                this.tables = result;

                // liste des colonnes pour le filtrage
                this.tables.forEach((table) => {
                    const colForTable: FieldTableDto[] = table.fields
                        .filter(c => !(c.fk || c.pk || c.categoryField === CategoryFieldEnum.TECHNICAL))
                        .map(c => c as FieldTableDto);
                    colForTable.forEach((col) => {
                        col.table = table.code;
                        col.schema = table.schema;
                        col.key = `${col.table}.${col.code}`;
                    });
                    this.cols.push(...colForTable);
                });
                this.cols = _.uniqBy(this.cols, 'code');

                setTimeout(() => {
                    if (this.mode === 'MODE_EXPORT' && this.customHeader && this.customHeader.length > 0) {
                        // En mode export, récupérer les colonnes depuis etl.transforms
                        this.colsSelected = this.getColumnsFromEtlTransforms();
                    } else {
                        // En mode view
                        this.colsSelected = this.cols.filter((el) => this.displayablesOrigin.includes(el.code)).sort(
                            (a, b) => this.displayablesOrigin.indexOf(a.code) - this.displayablesOrigin.indexOf(b.code)
                        );
                    }

                    this.emitSelectedColumns();
                    this.cdref.detectChanges();
                }, 0);

                // liste des colonnes pour l'affichage
                if (this.selectColumns) {
                    const tableDisplay: string[] = this.selectColumns.map(
                        (col) => col.tableName
                    );
                    this.selectCols = [];
                    this.tables
                        .filter((t) => tableDisplay.includes(t.code))
                        .forEach((table) => {
                            const colForTable: FieldTableDto[] = table.fields
                                .filter(c => !(c.fk || c.pk || c.categoryField === CategoryFieldEnum.TECHNICAL))
                                .map(c => c as FieldTableDto);
                            colForTable
                                .forEach((col) => {
                                    col.table = table.code;
                                    col.schema = table.schema;
                                });
                            this.selectCols.push(...colForTable);
                        });
                    //
                } else {
                    this.selectCols = this.cols;
                }
                this.updateSelectColsGrouped();

                //
                // this.dataView.
                this.expandSortField = [];
                this.cols.forEach((f) => {
                    if (!f.code.startsWith('id')) {
                        this.expandSortField.push(f.code);
                    }
                });
                this.updateQuery();
                //
                this.compareService.init();
                this.compareService.colsObs.next(this.cols);

                this.dataFetched = true;
                setTimeout(() => {
                    this.display = true;
                    this.cdref.detectChanges();
                }, 0);
            });
        //
        this.compareService.countObs.subscribe((nb) => {
            this.labelComparateur = this.translate.instant('components.table-meta.compareLabel', {n: nb});
            this.disableComparateur = nb === 0 ? true : false;
        });
    }

    changeRowByPage(event) {
        this.paginator = {
            pageSize: this.selectedRowByPage.value,
            start: 0,
        };
        this.search(false);
    }

    /**
     * Met à jour la requête et déclenche une recherche avec les contraintes et options fournies.
     * Combine la contrainte fournie avec la contrainte de recherche globale (si présente) en utilisant la logique AND.
     * 
     * @param constraint Contrainte optionnelle (filtre) provenant des filtres avancés. Si null, efface la contrainte existante.
     * @param ordering Tableau optionnel de règles de tri pour ordonner les résultats.
     * @param cols Tableau optionnel de clés de colonnes à afficher.
     * @param having Tableau optionnel de contraintes HAVING pour le regroupement.
     */
    updateQuery(constraint?: Constraint,
        ordering?: Array<Ordering>,
        cols?: Array<string>,
                having?: HavingCountTable[]) {
        const query: ObjectQueryFilter = {};

        if (constraint === null) {
            this.currentConstraint = null;
        } else if (constraint != null) {
            this.currentConstraint = constraint;
        }

        // Construire la contrainte de recherche globale
        const globalSearchConstraint = this.buildGlobalSearchConstraint(this.globalSearchText);

        // Combiner la contrainte existante avec la contrainte de recherche globale
        const combinedConstraint = this.combineConstraints(this.currentConstraint, globalSearchConstraint);
        
        if (combinedConstraint) {
            query.constraint = combinedConstraint;
        }
        if (ordering !== undefined && JSON.stringify(ordering) !== JSON.stringify(this.orderings)) {
            this.orderings = ordering;
            this.multiSortMeta = this.filterBuilderService.buildSortMetasByOrdering(this.orderings);
        } else if (ordering === null) {
            this.orderings = [];
            this.multiSortMeta = [];
        }
        if (cols && cols.length > 0) {
            const colsSelected = [];
            cols.forEach(col =>
                this.selectColsGrouped.forEach(g =>
                    g.items.filter(item => item.key === col)
                        .forEach(item => colsSelected.push(item))
                )
            );
            this.colsSelected = colsSelected.length > 0 ? colsSelected : this.colsSelected;
        }
        if (having !== undefined && JSON.stringify(this.having) !== JSON.stringify(having)) {
            this.having = having;
        }

        query.havingCountTable = this.having;
        query.orderings = this.orderings;

        this.loading = true;
        this.callSearchQuery(query);
    }

    loadLazy(event: LazyLoadEvent) {
        this.orderings = this.filterBuilderService.buildOrderings(event.multiSortMeta);
        this.paginator.start = event.first / this.paginator.pageSize;
        this.updateQuery();
    }

    expandChangeSortField(items) {
        this.expandSortField = items;
        if (this.compareSortField.length === 0) {
            this.compareSortField = this.expandSortField;
        }
    }

    changeCompareSortField(items) {
        this.compareSortField = items;
    }

    /**
     * Construit une contrainte de recherche qui recherche dans toutes les colonnes visibles
     * Si plusieurs mots sont saisis, chaque mot est recherché séparément avec OR
     * @param searchText Le texte à rechercher
     * @returns OrConstraint avec FullTextSearchConstraint pour chaque colonne et chaque mot, ou null si la recherche est vide
     */
    private buildGlobalSearchConstraint(searchText: string): Constraint | null {
        if (!searchText || searchText.trim().length === 0) {
            return null;
        }

        // Découper uniquement sur les espaces : pas d'heuristique sur le contenu (nom, email, etc.)
        const words = searchText.trim().split(/\s+/).filter((word) => word.length > 0);

        if (words.length === 0) {
            return null;
        }

        const searchConstraints: FullTextSearchConstraint[] = [];

        if (this.colsSelected && this.colsSelected.length > 0) {
            this.colsSelected.forEach((col: FieldTableDto) => {
                if (col.categoryField !== CategoryFieldEnum.TECHNICAL) {
                    words.forEach((word) => {
                        const fullTextSearch: FullTextSearchConstraint = {
                            '@type': 'FullTextSearchConstraint',
                            propertyName: `${col.table}.${col.code}`,
                            fullTextSearchExpression: {
                                '@type': 'Value',
                                typeValue: 'STRING',
                                value: word
                            } as Value
                        };
                        searchConstraints.push(fullTextSearch);
                    });
                }
            });
        }

        if (searchConstraints.length === 0) {
            return null;
        }

        if (searchConstraints.length === 1) {
            return searchConstraints[0];
        }

        return {
            '@type': 'OrConstraint',
            constraints: searchConstraints
        } as OrConstraint;
    }

    /**
     * Combine la contrainte de recherche globale avec la contrainte existante en utilisant AND
     * @param existingConstraint La contrainte existante (provenant des filtres)
     * @param searchConstraint La contrainte de recherche (provenant de la recherche globale)
     * @returns Contrainte combinée ou contrainte unique si une seule existe
     */
    private combineConstraints(existingConstraint: Constraint | null, searchConstraint: Constraint | null): Constraint | null {
        if (!existingConstraint && !searchConstraint) {
            return null;
        }
        if (!existingConstraint) {
            return searchConstraint;
        }
        if (!searchConstraint) {
            return existingConstraint;
        }

        // Les deux existent, combiner avec AND
        return {
            '@type': 'AndConstraint',
            constraints: [existingConstraint, searchConstraint]
        } as AndConstraint;
    }

    /**
     * Gère les changements de saisie de la recherche globale
     * @param event L'événement de saisie
     */
    onGlobalSearchChange(event: any) {
        const searchText = event.target.value || '';
        this.globalSearchText = searchText;
        // Réinitialiser à la première page lors de la recherche
        this.paginator.start = 0;
        // Déclencher la recherche avec la contrainte existante
        this.updateQuery(this.currentConstraint);
    }

    /**
     * Efface la recherche globale
     */
    clearGlobalSearch() {
        this.globalSearchText = '';
        this.paginator.start = 0;
        this.updateQuery(this.currentConstraint);
    }

    /**
     * Met le focus sur l'input de recherche
     */
    focusSearchInput() {
        if (this.searchInput && this.searchInput.nativeElement) {
            this.searchInput.nativeElement.focus();
        }
    }

    toggleRowExpansion(rowData: any, event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const key = String(rowData?.['@id'] ?? '');
        if (!key) {
            return;
        }
        if (this.expandedRowKeys[key]) {
            delete this.expandedRowKeys[key];
        } else {
            this.expandedRowKeys[key] = true;
        }
        this.expandedRowKeys = {...this.expandedRowKeys};
        this.cdref.markForCheck();
    }

    /**
     * Met en surbrillance le texte recherché dans une valeur
     * @param value La valeur à mettre en surbrillance
     * @returns SafeHtml avec les mots recherchés mis en surbrillance
     */
    highlightSearchText(value: any): SafeHtml {
        if (!value || !this.globalSearchText || this.globalSearchText.trim().length === 0) {
            const plainText = value != null ? String(value) : '';
            return this.sanitizer.bypassSecurityTrustHtml(plainText);
        }

        const textValue = String(value);
        const searchWords = this.globalSearchText.trim().split(/\s+/).filter((word) => word.length > 0);
        
        if (searchWords.length === 0) {
            return this.sanitizer.bypassSecurityTrustHtml(textValue);
        }

        // Échapper d'abord les caractères HTML spéciaux dans le texte brut
        const escapedText = textValue
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

        // Trouver toutes les positions de correspondance AVANT de créer les balises
        interface Match {
            start: number;
            end: number;
            text: string;
        }
        
        const matches: Match[] = [];
        const sortedWords = [...searchWords].sort((a, b) => b.length - a.length);
        
        // Trouver toutes les correspondances pour tous les mots
        sortedWords.forEach((word) => {
            const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapedWord, 'gi');
            let match;
            
            while ((match = regex.exec(escapedText)) !== null) {
                matches.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    text: match[0]
                });
            }
        });
        
        // Trier les correspondances par position de début
        matches.sort((a, b) => a.start - b.start);
        
        // Fusionner les correspondances qui se chevauchent
        const mergedMatches: Match[] = [];
        matches.forEach((match) => {
            const lastMatch = mergedMatches[mergedMatches.length - 1];
            if (lastMatch && match.start <= lastMatch.end) {
                // Chevauchement : fusionner en prenant la plus grande étendue
                lastMatch.end = Math.max(lastMatch.end, match.end);
            } else {
                mergedMatches.push({...match});
            }
        });
        
        // Construire le texte avec les balises mark en une seule passe
        let highlightedText = '';
        let lastIndex = 0;
        
        mergedMatches.forEach((match) => {
            // Ajouter le texte avant la correspondance
            highlightedText += escapedText.substring(lastIndex, match.start);
            // Ajouter la correspondance avec la balise mark
            highlightedText += `<mark class="search-highlight">${match.text}</mark>`;
            lastIndex = match.end;
        });
        
        // Ajouter le reste du texte
        highlightedText += escapedText.substring(lastIndex);

        return this.sanitizer.bypassSecurityTrustHtml(highlightedText);
    }

    /**
     * Vérifie si une valeur contient le texte recherché
     * @param value La valeur à vérifier
     * @returns true si la valeur contient au moins un mot recherché
     */
    containsSearchText(value: any): boolean {
        if (!value || !this.globalSearchText || this.globalSearchText.trim().length === 0) {
            return false;
        }

        const textValue = String(value).toLowerCase();
        const searchWords = this.globalSearchText.trim().toLowerCase().split(/\s+/).filter((word) => word.length > 0);
        
        return searchWords.some(word => textValue.includes(word));
    }

    search(clearSaveQuery = true) {
        this.updateQuery(this.currentConstraint);
    }

    openDetailFromChild(rowData, rowIndex) {
        this.openDetail(rowData, rowIndex);
    }

    openDetail(rowData, index) {
        const me = this;
        this.ref = this.dialogService.open(DynRawDetailDialogComponent, {
            header: this.translate.instant('components.table-meta.detailsHeader'),
            closable: true,
            data: {
                currentQueryFilterCache: this.currentQueryFilterCache,
                partner: this.partner,
                datastore: this.datastore,
                dataView: this.dataView,
                tables: this.tables,
                id: rowData['@id'],
                refreshData: () => {
                    me.search();
                }
            } as RawDetailInfoInterface,
            width: '90%',
            height: '90%',
            contentStyle: { 'max-height': '90%', overflow: 'auto' }
        });
    }

    /**
     *
     * @param tables liste des jointures de tables
     * @param tableSource table parente
     */
    protected setTableRecursif(tables: Array<TableJointure>, tableSource) {
        if (tables) {
            if (this.tableAvecPlusieursJointures == null && tables.length > 1) {
                this.tableAvecPlusieursJointures = tableSource;
            }
            tables.forEach((j) => {
                this.codeTables.push(j.tableName);
                this.setTableRecursif(j.join, j);
            });
        }
    }

    /**
     * Initialise la pagination
     */
    public initPaginator(onlyStartPage = false) {
        if (onlyStartPage) {
            this.paginator.start = 0;
        } else {
            this.selectedRowByPage = this.rowByPages[1];
            this.paginator = {
                pageSize: this.selectedRowByPage.value,
                start: 0,
            };
        }
    }

    private callSearchQuery(query: ObjectQueryFilter) {
        this.loading = true;
        const currentQueryFilter: ObjectQueryFilter = {
            orderings: query.orderings,
            constraint: query.constraint,
            havingCountTable: query.havingCountTable
        };
        this.currentQueryFilterCache = currentQueryFilter;
        const cols = this.colsSelected ? this.colsSelected.map((el) => el.code) : [];
        this.onQueryUpdate.emit({
            datastore: this.datastore,
            partner: this.partner,
            dataview: this.dataView.code,
            dataviewId: this.dataView.id,
            start: this.paginator.start,
            filter: currentQueryFilter,
            end: this.paginator.pageSize,
            cols: cols
        });
        this.engineDataQueryService.getQueryDataByDataviewUsingPOST(this.datastore, this.partner, this.idDataview, this.paginator.start,
            currentQueryFilter, this.paginator.pageSize, 'response', false, this.partners?.length ? this.partners : undefined)
            .subscribe((data) => {
                this.loading = false;
                this.totalRecords = +data.headers.get('x-total-count');
                this.rowsData = [];
                for (const rowData of data.body || []) {
                    const rowValues = rowData.rows || {};
                    rowValues['@id'] = rowData.id;
                    this.rowsData.push(rowValues);
                }
                this.loadRowTags(this.rowsData);
                this.expandedRowKeys = {};
                this.onDataLoaded.emit(this.rowsData);
                // this.display = true;
                // this.cdref.detectChanges();

                // Définir display à true comme dernière opération
                setTimeout(() => {
                    this.display = true;
                    this.cdref.detectChanges();
                }, 0);
            }, () => {
                // Le message est affiché par ErrorsInterceptor (ex. filtre de comptage impossible).
                // Sans ce retour, le tableau restait en chargement ; et garder les lignes précédentes
                // les présenterait comme le résultat des filtres affichés, qui n'ont pas pu s'appliquer.
                this.loading = false;
                this.rowsData = [];
                this.totalRecords = 0;
                this.cdref.detectChanges();
            });
    }

    openNewData() {
        const me = this;
        this.ref = this.dialogService.open(DynRawNewDialogComponent, {
            header: this.translate.instant('components.table-meta.addItemHeader', {item: this.labelAddItem}),
            data: {
                currentQueryFilterCache: this.currentQueryFilterCache,
                partner: this.partner,
                datastore: this.datastore,
                dataView: this.dataView,
                tables: this.tables,
                refreshData: () => {
                    me.search();
                }
            } as RawNewInfoInterface,

            closable: true,
            // Le formulaire fait une colonne de libellés + une colonne de champs : au-delà, les
            // champs s'étirent sans rien apporter et la modale couvre inutilement la page.
            width: '44rem',
            breakpoints: { '960px': '75vw', '640px': '95vw' },
            contentStyle: { 'max-height': '80vh', overflow: 'auto' }
        });
    }

    handleCreateButtonClick() {
        if (this.isCreateButtonDisabled) {
            this.displayCreateDisabledDialog = true;
        } else {
            this.openNewData();
        }
    }

    navigateToViewSettings() {
        if (this.datastore && this.idDataview) {
            this.displayCreateDisabledDialog = false;
            this.router.navigate(['/h/settings/view-editor', this.datastore, this.idDataview]);
        }
    }

    calculateEndRecord(): number {
        const start = this.paginator.start;
        const pageSize = this.paginator.pageSize;

        return Math.min(start + pageSize, this.totalRecords);
    }

    public getValueHeaderForExport(col: FieldTableDto): string {
        const header = this.getHeaderTransform(col);
        if (header && header.headerDest) {
            return header.headerDest;
        }
        return '';
    }

    public updateValueHeaderForExport(col: FieldTableDto, $event): void {
        const value = $event.currentTarget.value;
        let header = this.getHeaderTransform(col);
        if (header) {
            header.headerDest = value;
        } else {
            header = {
                headerDest: value,
                tableDest: col.table,
                fieldDest: col.code,
                typeTransform: 'ReplaceHeaderTransformConf'
            };
            this.customHeader.push(header);
        }
        this.onCustomHeaderChange.emit(this.customHeader);
    }

    private getHeaderTransform(col: FieldTableDto): ReplaceHeaderTransformConf | undefined {
        const header = this.customHeader.filter(h => col.key === `${h.tableDest}.${h.fieldDest}`);
        if (header && header.length === 1) {
            return header[0];
        }
        return undefined;
    }

    openColumnManager() {
        const selectedCodes = new Set(this.colsSelected.map(col => col.code));
        const selected = this.colsSelected.map(sel => this.selectCols.find(col => col.code === sel.code)).filter(Boolean);
        const unselected = this.selectCols.filter(col => !selectedCodes.has(col.code));
        this.selectCols = [...selected, ...unselected];
        this.columnManagerDialog = true;
    }

    onReorder(event: any) {
        const selectedCodes = new Set(this.colsSelected.map(col => col.code));
        this.colsSelected = this.selectCols.filter(col => selectedCodes.has(col.code));
        this.emitSelectedColumns();
    }

    isEditing(column: FieldTableDto): boolean {
        const columnKey = this.getColumnKey(column);
        return this.columnEditState[columnKey]?.editing === true;
    }

    startEditing(column: FieldTableDto): void {
        const columnKey = this.getColumnKey(column);
        const currentValue = this.getValueHeaderForExport(column);

        this.columnEditState[columnKey] = {
            editing: true,
            editValue: currentValue || (column.label || column.code)
        };

        this.cdref.detectChanges();
    }

    getEditValue(column: FieldTableDto): string {
        const columnKey = this.getColumnKey(column);
        return this.columnEditState[columnKey]?.editValue || '';
    }

    updateEditValue(column: FieldTableDto, value: string): void {
        const columnKey = this.getColumnKey(column);
        if (this.columnEditState[columnKey]) {
            this.columnEditState[columnKey].editValue = value;
        }
    }

    saveEdit(column: FieldTableDto): void {
        const columnKey = this.getColumnKey(column);
        const editState = this.columnEditState[columnKey];

        if (editState?.editing && editState.editValue) {
            let header = this.getHeaderTransform(column);
            if (header) {
                header.headerDest = editState.editValue;
            } else {
                header = {
                    headerDest: editState.editValue,
                    tableDest: column.table,
                    fieldDest: column.code,
                    typeTransform: 'ReplaceHeaderTransformConf'
                };
                this.customHeader.push(header);
            }
            this.onCustomHeaderChange.emit(this.customHeader);
        }

        delete this.columnEditState[columnKey];
        this.cdref.detectChanges();
    }

    cancelEdit(column: FieldTableDto): void {
        const columnKey = this.getColumnKey(column);
        delete this.columnEditState[columnKey];
        this.cdref.detectChanges();
    }

    cancelAllEditing(): void {
        this.columnEditState = {};
    }

    private getColumnKey(column: FieldTableDto): string {
        return `${column.table}.${column.code}`;
    }

    onColumnsSelectionChange() {
        const selectedCodes = new Set(this.colsSelected.map(col => col.code));
        const selected = this.colsSelected.map(sel => this.selectCols.find(col => col.code === sel.code)).filter(Boolean);

        const unselected = this.selectCols.filter(col => !selectedCodes.has(col.code));
        this.selectCols = [...selected, ...unselected];
        this.emitSelectedColumns();
        
        // Si une recherche globale est active, relancer la recherche avec les nouvelles colonnes
        if (this.globalSearchText && this.globalSearchText.trim().length > 0) {
            this.updateQuery(this.currentConstraint);
        }
    }

    emitSelectedColumns() {
        this.onSelectedColumnsChange.emit(this.colsSelected);
    }

    /**
     * Récupère les colonnes sélectionnées avec leurs libellés d'export
     */
    public getSelectedColumnsWithExportLabels(): ReplaceHeaderTransformConf[] {
        return this.colsSelected.map(col => ({

            column: col,
            headerDest: this.getValueHeaderForExport(col) || (col.label || col.code),
            tableDest: col.table,
            fieldDest: col.code,
            typeTransform: 'ReplaceHeaderTransformConf'
        } as ReplaceHeaderTransformConf
        ));
    }
    
    /**
     * Récupère les colonnes sélectionnés depuis etl.transform
     */
    private getColumnsFromEtlTransforms():FieldTableDto[]{
        const selectedColumns: FieldTableDto[] = [];
        this.customHeader.forEach(header => {
            if(header.headerDest && header.tableDest){
                const matchingColumn = this.cols.find(col => col.code === header.fieldDest && col.table === header.tableDest);
                if(matchingColumn) {
                    selectedColumns.push(matchingColumn);
                }
            }
        });
        return selectedColumns;
    }

    getAcquisitionBulkScope(): 'PERSONAL' | 'COMMAND' | null {
        const t = (this.dataView?.table?.tableName || '').toLowerCase();
        const code = (this.dataView?.code || '').toLowerCase();
        if (t.includes('personal') || code.includes('personal')) {
            return 'PERSONAL';
        }
        if (t.includes('command') || code.includes('command')) {
            return 'COMMAND';
        }
        return null;
    }

    openManualAcquisitionBulk(): void {
        this.openManualAcquisitionBulkWithFilter(this.currentQueryFilterCache || {}, false);
    }

    openManualAcquisitionBulkForExpandedRow(rowData: any): void {
        if (!rowData) {
            return;
        }
        const queryFilter = this.buildManualAcquisitionQueryFilterForRow(rowData);
        this.openManualAcquisitionBulkWithFilter(queryFilter, true);
    }

    private buildManualAcquisitionQueryFilterForRow(rowData: any): ObjectQueryFilter {
        const base: ObjectQueryFilter = this.currentQueryFilterCache
            ? JSON.parse(JSON.stringify(this.currentQueryFilterCache))
            : {};
        if (!base.orderings) {
            base.orderings = [];
        }
        const table = this.dataView.table.tableName;
        const mainId = rowData['@id'];
        const rowConstraint: ComparisonConstraint = {
            '@type': 'ComparisonConstraint',
            operand1: { '@type': 'PropertyValue', propertyName: `${table}.id` } as PropertyValue,
            operand2: { '@type': 'Value', typeValue: 'DOUBLE', value: mainId } as Value,
            operator: 'EQUALS'
        };
        base.constraint = this.combineConstraints(base.constraint ?? null, rowConstraint);
        return base;
    }

    private openManualAcquisitionBulkWithFilter(queryFilter: ObjectQueryFilter, singleRowMode: boolean): void {
        const scope = this.getAcquisitionBulkScope();
        if (!scope || !this.idDataview || !this.datastore || !this.partner) {
            return;
        }
        this.dialogService.open(ManualAcquisitionBulkDialogComponent, {
            header: this.translate.instant('manualAcquisition.bulk.dialogTitle'),
            width: 'min(1100px, 95vw)',
            contentStyle: { 'max-height': '90vh', overflow: 'auto' },
            closable: true,
            closeOnEscape: false,
            dismissableMask: false,
            data: {
                tenant: this.datastore,
                partner: this.partner,
                dataviewId: this.idDataview,
                queryFilter,
                scope,
                initialPageSize: this.paginator?.pageSize,
                singleRowMode
            } as ManualAcquisitionBulkDialogData
        });
    }

    showManualAcquisitionRowButton(): boolean {
        return !!this.getAcquisitionBulkScope() && !!this.display && !!this.dataView?.id;
    }

    /** Sur une vue de commandes, la colonne montre les tags du client : le tag appartient au contact. */
    get tagsColumnHeaderKey(): string {
        return this.isPersonalMainTable
            ? 'components.table-meta.tagsColumnHeader'
            : 'components.table-meta.customerTagsColumnHeader';
    }

    get isPersonalMainTable(): boolean {
        return this.dataView?.table?.tableName === TABLE_PERSONAL;
    }

    /**
     * Tag ponctuel des résultats (bouton « Taguer »), ou règle sur le preset chargé (« Automatiser »,
     * ouvert par la barre de preset). Le résultat est annoncé et la liste rechargée ici ; l'appelant
     * reçoit aussi le résultat (la barre de preset recharge ses règles).
     */
    openTagResults(options: { rule?: TagRuleTarget; initialStatus?: boolean; initialTagIds?: number[] } = {}):
        Observable<TagResultsDialogResult> {
        // un seul tag à la fois depuis la page : le résultat de celui en cours reste à annoncer
        if (this.tagJob) {
            this.messageService.add({severity: 'info', summary: this.translate.instant('tagResults.job.alreadyRunning')});
            return EMPTY;
        }
        const header = options.rule
            ? this.translate.instant('tagResults.rule.dialogTitle', {preset: options.rule.presetName})
            : this.translate.instant('tagResults.dialogTitle');
        const closed = this.dialogService.open(TagResultsDialogComponent, {
            header,
            closable: true,
            width: '40rem',
            breakpoints: { '640px': '95vw' },
            data: {
                tenant: this.datastore,
                partner: this.partner,
                dataviewId: this.idDataview,
                queryFilter: this.currentQueryFilterCache || {},
                // Sans `parent` : l'arbre est circulaire, et on ne modifie pas celui de l'écran.
                queryFilterUi: this.filterTree
                    ? JSON.stringify(this.filterTree, (key, value) => key === 'parent' ? undefined : value)
                    : '{}',
                totalRecords: this.totalRecords,
                personalView: this.isPersonalMainTable,
                ...options
            } as TagResultsDialogData
        }).onClose.pipe(take(1));
        // le tenant du lancement : celui de la page peut changer avant la fin de la tâche
        const tenant = this.datastore;
        closed.subscribe((result: TagResultsDialogResult) => {
            if (result) {
                this.followTagJob(tenant, result);
            }
        });
        return closed;
    }

    /** Libellé du bouton « Taguer » : l'avancement de la tâche en cours, s'il y en a une. */
    get tagButtonLabel(): string {
        if (!this.tagJob) {
            return this.translate.instant('tagResults.buttonLabel');
        }
        const percent = tagJobPercent(this.tagJob);
        return percent == null
            ? this.translate.instant('tagResults.job.starting')
            : this.translate.instant('tagResults.job.running', {percent});
    }

    /**
     * Suit l'application lancée par la modale jusqu'à sa fin, puis annonce le résultat réel et recharge la
     * liste. L'engine l'applique en tâche de fond : sur ~100 000 contacts elle dure, et la page reste
     * utilisable pendant ce temps.
     */
    private followTagJob(tenant: string, result: TagResultsDialogResult): void {
        this.tagJobSub?.unsubscribe();
        this.tagJobSub = this.tagJobService.watch(tenant, result.job).subscribe({
            next: job => {
                this.tagJob = isTagJobFinished(job) ? null : job;
                if (job.status === 'COMPLETED') {
                    if (result.rule) {
                        this.showRuleResult(result, job.changes);
                    } else {
                        this.showTagResult(result, job.changes);
                    }
                    if (result.applied) {
                        this.search();
                    }
                } else if (job.status === 'FAILED') {
                    this.showTagJobFailure(result.rule ? 'failedRule' : 'failed', job.changes);
                    // ce qui a été appliqué avant l'arrêt l'est pour de bon
                    this.search();
                }
                this.cdref.markForCheck();
            },
            // suivi perdu (engine redémarré, réseau coupé) : l'application a pu aboutir malgré tout
            error: () => {
                this.tagJob = null;
                this.showTagJobFailure('lost', 0);
                this.cdref.markForCheck();
            }
        });
    }

    private showTagJobFailure(reason: 'failed' | 'failedRule' | 'lost', changes: number): void {
        this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('tagResults.job.failedSummary'),
            detail: this.translate.instant(`tagResults.job.${reason}`, {count: changes.toLocaleString('fr-FR')}),
            // à lire : il dit quoi faire ensuite
            sticky: true
        });
    }

    /** Proposition « Automatiser » d'un message de résultat : la barre de preset ouvre la règle. */
    requestAutomate(message: { data?: { status: boolean; tagIds: number[] } }): void {
        this.messageService.clear(TAG_RESULT_TOAST);
        this.automateRequested.emit(message.data);
    }

    private showTagResult(result: TagResultsDialogResult, changes: number): void {
        const action = result.status ? 'add' : 'remove';
        // Un preset est chargé : le message propose de refaire ce tag automatiquement sur lui.
        const automate = this.activePreset
            ? {key: TAG_RESULT_TOAST, life: 10000, data: {status: result.status, tagIds: result.tagIds,
                presetName: this.activePreset.name}}
            : {};
        if (changes === 0) {
            this.messageService.add({
                severity: 'info',
                summary: this.translate.instant('tagResults.done.noChangeSummary'),
                detail: this.translate.instant(`tagResults.done.noChange.${action}`),
                ...automate
            });
            return;
        }
        const tags = result.tagNames.length === 1 ? 'oneTag' : 'manyTags';
        const detail = this.translate.instant(`tagResults.done.${tags}.${action}.${changes === 1 ? 'one' : 'other'}`,
            {tag: result.tagNames[0], count: changes.toLocaleString('fr-FR')});
        this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('tagResults.done.summary'),
            detail,
            ...automate
        });
    }

    private showRuleResult(result: TagResultsDialogResult, changes: number): void {
        let detail: string;
        if (result.applied) {
            detail = changes === 0
                ? this.translate.instant('tagResults.rule.done.appliedNoChange')
                : this.translate.instant(`tagResults.rule.done.applied.${changes === 1 ? 'one' : 'other'}`,
                    {count: changes.toLocaleString('fr-FR')});
        } else {
            detail = this.translate.instant(result.scheduled ? 'tagResults.rule.done.scheduled' : 'tagResults.rule.done.onDemand');
        }
        this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('tagResults.rule.done.summary'),
            detail
        });
    }

    onShowTagsColumnChange(): void {
        this.writeShowTagsColumn(this.showTagsColumn);
        this.loadRowTags(this.rowsData);
    }

    /** Un seul appel par page : les tags de tous les contacts affichés. */
    private loadRowTags(rows: any[]): void {
        if (!this.showTagsColumn || !rows?.length) {
            return;
        }
        const personalIds = [...new Set(rows.map(row => this.getRowPersonalId(row)).filter(id => id != null))];
        if (personalIds.length === 0) {
            rows.forEach(row => {
                row[ROW_TAGS_KEY] = [];
            });
            return;
        }
        this.engineTagEventPersonalService.findTagsByPersonals(personalIds, this.datastore).subscribe(tags => {
            const namesByPersonal = new Map<number, string[]>();
            tags.forEach(tag => {
                const personalId = Number(tag.personalId);
                namesByPersonal.set(personalId, [...(namesByPersonal.get(personalId) ?? []), tag.tagName]);
            });
            rows.forEach(row => {
                row[ROW_TAGS_KEY] = namesByPersonal.get(this.getRowPersonalId(row)) ?? [];
            });
            this.cdref.markForCheck();
        });
    }

    private getRowPersonalId(row: any): number | null {
        const id = row['personal.id'] ?? (this.isPersonalMainTable ? row['@id'] : null);
        return id != null ? Number(id) : null;
    }

    private showTagsColumnStorageKey(): string {
        return `tableMetaShowTags.${this.codeView}`;
    }

    /** Préférence de l'utilisateur, par vue. La colonne est affichée tant qu'il ne l'a pas masquée. */
    private readShowTagsColumn(): boolean {
        try {
            return localStorage.getItem(this.showTagsColumnStorageKey()) !== 'false';
        } catch {
            return true;
        }
    }

    private writeShowTagsColumn(show: boolean): void {
        try {
            localStorage.setItem(this.showTagsColumnStorageKey(), String(show));
        } catch {
            // Stockage indisponible (navigation privée) : le choix vaut jusqu'au rechargement.
        }
    }
}
